import React, { useState, useEffect, useRef } from "react";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { FaCircle, FaCalendar, FaUser, FaSuitcase } from "react-icons/fa";
import { MdAccessTimeFilled } from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Map from "./components/Map"; // Ensure this component handles direction rendering
import "./style.css";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import {
  setPickup,
  setDropoff,
  setDistance,
  setDuration,
  setServiceType,
  setPassenger,
  setLuggage,
  setBooster,
  setRegular,
  setInfant,
  setDate,
  setTime,
} from "../features/bookingSlice/bookingSlice";

const FirstStep = () => {
  const navigate = useNavigate();

  // Google Maps API loader
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  // Fixed input value for Boston Logan Airport
  const fixedInputValue = "Boston Logan International Airport";
  const fixedCoordinates = { lat: 42.3656338, lng: -71.0282381 };

  const [direction, setDirection] = useState(null);
  const [isOn, setIsOn] = useState(false);
  const selected = useSelector((state) => state.booking.service_type);
  const { passenger, luggage, booster, regular, infant, date, time } = useSelector((state) => state.booking);
  const [localDate, setLocalDate] = useState(date ? new Date(date) : null);
  const [localTime, setLocalTime] = useState(time);

  // Redux state
  const { pickup, dropoff, distance, duration } = useSelector(
    (state) => state.booking
  );
  const dispatch = useDispatch();
  
  const handleChange = (e) => {
    const newServiceType = e.target.value;
    dispatch(setServiceType(newServiceType));

    if (newServiceType === "from_airport") {
      dispatch(setDropoff("")); // Clear the other field
      dispatch(setPickup({ address: fixedInputValue, coordinates: fixedCoordinates }));
    } else { // to_airport
      dispatch(setPickup("")); // Clear the other field
      dispatch(setDropoff({ address: fixedInputValue, coordinates: fixedCoordinates }));
    }
  };

  const handleToggle = () => {
    setIsOn((prev) => !prev);
  };

  const handleDateChange = (date) => {
    setLocalDate(date);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const formattedDate = `${month}/${day}/${year}`;
    dispatch(setDate(formattedDate));
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hour24 = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        let hour12 = h % 12 || 12;
        let ampm = h < 12 ? 'AM' : 'PM';
        const display = `${hour12.toString().padStart(2, '0')}:${minute} ${ampm}`;
        const value = `${hour24}:${minute}`;
        times.push({ value, display });
      }
    }
    return times;
  };
  const timeOptions = generateTimeOptions();
  const dateInputRef = useRef(null);
  const todayStr = new Date().toISOString().split('T')[0];
  
  // Autocomplete refs
  const autocompletePickup = useRef(null);
  const autocompleteDropoff = useRef(null);


  // Function to extract address and coordinates
  const handlePlaceChanged = (ref, actionCreator) => {
    if (ref.current) {
      const place = ref.current.getPlace();
      if (place && place.formatted_address && place.geometry) {
        const location = place.geometry.location;
        dispatch(
          actionCreator({
            address: place.formatted_address,
            coordinates: { lat: location.lat(), lng: location.lng() },
          })
        );
      } else {
        console.error("Invalid place selected.");
      }
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  // Calculate directions when both pickup and dropoff are set
  useEffect(() => {
    if (pickup?.coordinates && dropoff?.coordinates) {
      const calculateDirection = async () => {
        try {
          const directionsService = new window.google.maps.DirectionsService();
          const result = await directionsService.route({
            origin: pickup.coordinates,
            destination: dropoff.coordinates,
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL,
            region: 'US'
          });

          if (result.status === "OK") {
            setDirection(result);
            
            // Debug logging to compare with Google Maps
            console.log("🗺️ Route Debug Info:");
            console.log("Origin coordinates:", JSON.stringify(pickup.coordinates));
            console.log("Destination coordinates:", JSON.stringify(dropoff.coordinates));
            console.log("Calculated distance:", result.routes[0].legs[0].distance.text);
            console.log("Calculated duration:", result.routes[0].legs[0].duration.text);
            console.log("Expected Logan coords: {lat: 42.3631, lng: -71.0064}");
            
            dispatch(setDistance(result.routes[0].legs[0].distance.text));
            dispatch(setDuration(result.routes[0].legs[0].duration.text));
          } else {
            console.error("Directions request failed due to:", result.status);
          }
        } catch (error) {
          console.error(
            "Error calculating directions:",
            error.message || error
          );
        }
      };

      calculateDirection();
    }
  }, [pickup, dropoff]);

  return (
    <div className="container-fluid dim-bg main-quote-page">
      <div className="row">
        <div className="col-lg-5">
          <div className="floating-sidebar">
            <div className="sidebar-booking">
              <form>
                <div className="from-to">
                  <div className="from">
                    <input
                      type="radio"
                      id="from_airport"
                      name="service_type"
                      value="from_airport"
                      checked={selected === "from_airport"}
                      onChange={handleChange}
                    />
                    <label htmlFor="from_airport">
                      <span>FROM AIRPORT</span>
                    </label>
                  </div>
                  <div className="to">
                    <input
                      type="radio"
                      id="to_airport"
                      name="service_type"
                      value="to_airport"
                      checked={selected === "to_airport"}
                      onChange={handleChange}
                    />
                    <label htmlFor="to_airport">
                      <span>TO AIRPORT</span>
                    </label>
                  </div>
                </div>

                {/* Pickup Address Input */}
                <div className="field-wrapper start-form-wrapper">
                  <div className="input-group border-2 border-dark py-2">
                    <div className="input-group-prepend">
                      <span className="input-group-text mt-1">
                        <div className="circle-icon-green">
                          <FaCircle size={10} color="white" />
                        </div>
                      </span>
                    </div>
                    <div className="w-75">
                      {isLoaded && (
                        <Autocomplete
                          onLoad={(ref) => (autocompletePickup.current = ref)}
                          onPlaceChanged={() =>
                            handlePlaceChanged(autocompletePickup, setPickup)
                          }
                        >
                          <input
                            type="text"
                            className="form-control br-0 inputstyle"
                            placeholder="Pickup"
                            value={
                              selected === "from_airport"
                                ? fixedInputValue
                                : pickup.address || ""
                            }
                            onChange={(e) =>
                              dispatch(
                                setPickup({ ...pickup, address: e.target.value })
                              )
                            }
                            onKeyDown={handleKeyDown}
                          />
                        </Autocomplete>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dropoff Address Input */}
                <div className="input-group border-2 border-dark py-2">
                  <div className="input-group-prepend">
                    <span className="input-group-text mt-1">
                      <div className="circle-icon-red">
                        <FaCircle size={10} color="white" />
                      </div>
                    </span>
                  </div>
                  <div className="w-75">
                    {isLoaded && (
                      <Autocomplete
                        onLoad={(ref) => (autocompleteDropoff.current = ref)}
                        onPlaceChanged={() =>
                          handlePlaceChanged(autocompleteDropoff, setDropoff)
                        }
                      >
                        <input
                          type="text"
                          className="form-control br-0 inputstyle"
                          placeholder="Dropoff"
                          value={
                            selected !== "from_airport"
                              ? fixedInputValue
                              : dropoff.address || ""
                          }
                          onChange={(e) =>
                            dispatch(
                              setDropoff({ ...dropoff, address: e.target.value })
                            )
                          }
                          onKeyDown={handleKeyDown}
                        />
                      </Autocomplete>
                    )}
                  </div>
                </div>

                {/* Date and Time Inputs */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-group border-2 border-dark py-2 my-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text"><FaCalendar /></span>
                      </div>
                      <DatePicker
                        selected={localDate}
                        onChange={handleDateChange}
                        minDate={new Date()}
                        className="form-control br-0 inputstyle react-datepicker-ignore-onclickoutside"
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Select Date"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group border-2 border-dark py-2 my-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text mt-1"><MdAccessTimeFilled /></span>
                      </div>
                      <select
                        id="time"
                        value={localTime}
                        className="form-control br-0 inputstyle"
                        onChange={e => {
                          setLocalTime(e.target.value);
                          let [hours, minutes] = e.target.value.split(":");
                          hours = parseInt(hours, 10);
                          const period = hours >= 12 ? "pm" : "am";
                          const displayHour = hours % 12 || 12;
                          const formattedTime = `${displayHour}:${minutes}${period}`;
                          dispatch(setTime(formattedTime));
                        }}
                        required
                      >
                        <option value="">Select Time</option>
                        {timeOptions.map((t) => (
                          <option key={t.value} value={t.value}>{t.display}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Passenger and Luggage Inputs */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-group border-2 border-dark py-2 my-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text mt-1"><FaUser /></span>
                      </div>
                      <select
                        className="form-control br-0 inputstyle"
                        id="passengers"
                        value={passenger}
                        onChange={(e) => dispatch(setPassenger(e.target.value))}
                        required
                      >
                        <option value="0">Select Passengers</option>
                        {[...Array(7).keys()].map(i => <option key={i+1} value={i+1}>{i+1}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group border-2 border-dark py-2 my-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text mt-1"><FaSuitcase /></span>
                      </div>
                      <select
                        className="form-control br-0 inputstyle"
                        id="luggage"
                        value={luggage}
                        onChange={(e) => dispatch(setLuggage(e.target.value))}
                        required
                      >
                        <option value="0">Select Luggage</option>
                        {[...Array(10).keys()].map(i => <option key={i+1} value={i+1}>{i+1}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Child Seat Toggle and Inputs */}
                <div className="d-flex justify-content-between my-3" onClick={handleToggle}>
                  <span>Add Child Seat</span>
                  <div
                    className={`relative w-12 h-6 ${isOn ? "bg-green-700" : "bg-gray-500"} rounded-full p-1 transition-colors duration-300 cursor-pointer`}
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full transition-transform duration-300 ${isOn ? "transform translate-x-6" : ""}`}
                    ></div>
                  </div>
                </div>

                {isOn && (
                  <div className="w-full flex justify-between gap-2 mb-3">
                    <select
                      className="border-2 p-2 w-1/3"
                      id="booster"
                      value={booster}
                      onChange={(e) => dispatch(setBooster(e.target.value))}
                    >
                      <option value="">Booster</option>
                      {[...Array(3).keys()].map(i => <option key={i+1} value={i+1}>{i+1}</option>)}
                    </select>
                    <select
                      className="border-2 p-2 w-1/3"
                      id="regular"
                      value={regular}
                      onChange={(e) => dispatch(setRegular(e.target.value))}
                    >
                      <option value="">Regular</option>
                      {[...Array(3).keys()].map(i => <option key={i+1} value={i+1}>{i+1}</option>)}
                    </select>
                    <select
                      className="border-2 p-2 w-1/3"
                      id="infant"
                      value={infant}
                      onChange={(e) => dispatch(setInfant(e.target.value))}
                    >
                      <option value="">Infant</option>
                      {[...Array(3).keys()].map(i => <option key={i+1} value={i+1}>{i+1}</option>)}
                    </select>
                  </div>
                )}

                <div className="fix-btn-bg bg-success">
                  <div className="d-flex justify-content-between mb-1 ">
                    <button
                      className="btn-main w-full m-auto bg-transparent border-0 text-white py-3 outline-0 fw-bold"
                      onClick={(e) => {
                        navigate("/vehicleselection");
                        e.preventDefault();
                      }}
                    >
                      REQUEST A RIDE
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <Map
            direction={direction}
            pickup={pickup.coordinates}
            dropoff={dropoff.coordinates}
          />
        </div>
      </div>
    </div>
  );
};

export default FirstStep;